<template>
  <main class="grow sw">
    <!-- HERO -->
    <section class="sw-hero">
      <div class="sw-wrap">
        <h1>Move your client work from HoneyBook to Workspace369.</h1>
        <!-- Answer-first block: the direct answer AI answers quote, worded like the buyer prompt. -->
        <div class="sw-answer">
          <p class="sw-answer-label">Switching to a HoneyBook alternative?</p>
          <p>
            Most teams move from HoneyBook to Workspace369 in a few hours: export your contacts as a spreadsheet,
            bring them in with AI Import (you check every row first), rebuild the templates you send, and keep
            HoneyBook open until open invoices are paid. Cadet is $29/mo for one person; a small agency team of
            five pays $104/mo on Voyager.
          </p>
          <p>
            HoneyBook's trial is 30 days with no card (its pricing page, checked October 24, 2026); Workspace369's
            is 14 days and needs a card (then $74/month on Voyager until you cancel), so start it once your
            HoneyBook export is ready.
          </p>
        </div>
        <p class="sw-lede">
          Three changes pushed a lot of HoneyBook users to look around this year. In February 2025,
          HoneyBook raised prices across every plan — and the 20% legacy discount for long-time
          customers ended in February 2026. In August 2026, two-way texting arrived as an $8/month
          add-on, for numbers in the US and Canada. And HoneyBook switched off Time Tracker on September 8, 2026
          (it came back a week later, after members complained). If any of that stung, this page is the honest path out: what moves, what
          doesn't, and the exact order to do it in.
        </p>
        <div class="sw-cta">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup?plan=voyager')"
            class="sw-btn"
            data-conversion-event="start_free_click"
            data-conversion-label="Switch from HoneyBook hero CTA"
            data-conversion-location="/switch/honeybook"
            :data-conversion-variant="ctaVariant || undefined"
            style="min-width: min(18em, 100%)"
          >{{ ctaLabel }}</a>
          <NuxtLink :to="localePath('/compare/honeybook')" class="sw-link">
            Compare Workspace369 vs HoneyBook →
          </NuxtLink>
        </div>
        <TrialDisclosure plan="voyager" on-light />
        <ChatGptProof location="/switch/honeybook" />
        <p class="sw-updated">Last updated October 31, 2026</p>
        <CompetitorNotice competitor="HoneyBook" date="October 24, 2026" iso="2026-10-24" />
      </div>
    </section>

    <!-- HOW TO LEAVE: tool-neutral steps from HoneyBook's own help center, whatever you move to -->
    <section class="sw-checklist">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>Whatever you move to</p>
          <h2>How to leave HoneyBook</h2>
          <span class="sw-head-copy">
            Six steps that apply whichever tool you pick. The HoneyBook details come from its own help center (read October 31, 2026).
            The <NuxtLink :to="localePath('/blog/how-to-export-from-honeybook')">HoneyBook export checklist</NuxtLink>
            covers every export in detail.
          </span>
        </header>

        <ol class="sw-steps">
          <li v-for="(step, i) in leaveSteps" :key="step.title" class="sw-step">
            <span class="sw-step-num" aria-hidden="true">{{ i + 1 }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.copy }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- WHAT MOVES, WHAT DOESN'T -->
    <section class="sw-moves">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>The honest part first</p>
          <h2>What moves, what doesn't</h2>
          <span class="sw-head-copy">
            There is no one-click import from HoneyBook, and Workspace369 does not claim one. You
            bring your data as spreadsheets and AI Import does the column mapping. Most teams finish
            in a few hours.
          </span>
        </header>

        <div class="sw-moves-grid">
          <div class="sw-move-card sw-move-yes">
            <h3>Moves with you</h3>
            <ul>
              <li>
                <strong>Clients, products, expenses, invoices and proposals.</strong> Upload a CSV or
                XLSX and AI Import maps the columns to the right fields. You review the mapping
                first.
              </li>
              <li>
                <strong>The cost, before you commit.</strong> The import shows how much of your monthly usage it needs before it starts, so nothing runs on a surprise bill.
              </li>
            </ul>
          </div>

          <div class="sw-move-card sw-move-no">
            <h3>Doesn't move automatically</h3>
            <ul>
              <li>
                <strong>Project notes and files.</strong> AI Import doesn't bring these. Download them and attach them to the
                client record or project.
              </li>
              <li>
                <strong>Templates.</strong> Re-create the ones you actually send —
                for most people that's three to five.
              </li>
              <li>
                <strong>Recurring schedules and payment plans in progress.</strong> Set them up
                fresh on the new side.
              </li>
              <li>
                <strong>Automations.</strong> Rebuild the two or three that matter and let the
                rest go.
              </li>
              <li>
                <strong>Links you already sent clients.</strong> Old portal, proposal, and invoice
                links keep pointing at HoneyBook.
              </li>
              <li>
                <strong>Cards on file.</strong> Saved payment methods can't leave HoneyBook.
                Clients re-add their card the first time they pay a Workspace369 invoice.
              </li>
            </ul>
          </div>
        </div>

        <p class="sw-moves-note">
          Keep your HoneyBook account open until the last open invoice there is
          paid and you've downloaded what you need — your history stays viewable under HoneyBook's
          own terms while the account is active. And if you'd like a hand before you start,
          Workspace369 support can help you map what goes where.
        </p>
      </div>
    </section>

    <!-- TOUR: the switch path on real app screens -->
    <section class="sw-tour">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>See it before you switch</p>
          <h2>From HoneyBook export to a signed, paid client</h2>
          <span class="sw-head-copy">
            Five real Workspace369 screens with sample data, in the order you would use them. Click through at your own pace.
          </span>
        </header>
        <WalkthroughScreenTour
          :tour="switchFromHoneyBookTour"
          location="/switch/honeybook"
          conversion-label="Switch from HoneyBook"
          aria-label="Switch from HoneyBook tour steps"
          trial-url="https://app.workspace369.com/signup?plan=voyager"
          plan="voyager"
        />
      </div>
    </section>

    <!-- CHECKLIST -->
    <section class="sw-checklist">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>Step by step</p>
          <h2>Setting up Workspace369: the checklist</h2>
          <span class="sw-head-copy">
            Five steps, in order. Do them over a weekend and bill from Workspace369 on Monday.
          </span>
        </header>

        <ol class="sw-steps">
          <li v-for="(step, i) in steps" :key="step.title" class="sw-step">
            <span class="sw-step-num" aria-hidden="true">{{ i + 1 }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.copy }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- PHONE AND IPAD -->
    <section class="sw-mobile">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>Phone and iPad</p>
          <h2>Workspace369 on iPhone and iPad</h2>
        </header>
        <MobileAppProof location="/switch/honeybook" />
      </div>
    </section>

    <!-- STAY -->
    <section class="sw-stay sw-stay--tinted">
      <div class="sw-wrap sw-wrap-narrow">
        <header class="sw-head">
          <p>Fair is fair</p>
          <h2>Stay on HoneyBook if…</h2>
        </header>
        <div class="sw-stay-card">
          <ul>
            <li>
              <strong>You work solo and the Phone &amp; SMS add-on covers your texting.</strong> HoneyBook's add-on starts at
              $8/mo for members in the US and Canada, which can cost less than moving a setup that already works.
            </li>
            <li>
              <strong>Your leads convert through HoneyBook's files.</strong> If its interactive files and template gallery win
              you work, and you need no more than two added team members or a phone system, staying is reasonable.
            </li>
            <li>
              <strong>You rely on its photographer and event integrations.</strong> Pic-Time and Prismm are on HoneyBook's
              integrations list.
            </li>
            <li>
              <strong>You're mid-way through payment plans.</strong> Finish them in HoneyBook and switch between projects,
              not mid-stream.
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- PLANS -->
    <section class="sw-plans" data-sticky-cta-hide>
      <div class="sw-wrap">
        <header class="sw-head">
          <p>Pick your plan</p>
          <h2>Five plans, priced by seats</h2>
          <span class="sw-head-copy">
            Cadet through Commander start with a 14-day free trial. Pay annually and each plan drops by about 20%.
            Extra seats are $10 a month each from Voyager.
          </span>
        </header>

        <div class="sw-table-scroll">
          <table class="sw-table">
            <thead>
              <tr>
                <th scope="col">Plan</th>
                <th scope="col">Monthly</th>
                <th scope="col">Seats</th>
                <th scope="col">Pick it if…</th>
                <th scope="col"><span class="sw-sr">Trial</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in plans" :key="plan.name" :class="{ 'sw-row-popular': plan.popular }">
                <th scope="row">
                  {{ plan.name }}
                  <span v-if="plan.popular" class="sw-popular-tag">Recommended</span>
                </th>
                <!-- Phone cards repeat the column names; the header row is hidden there. -->
                <td><span class="sw-cell-label" aria-hidden="true">Monthly</span>{{ plan.price }}</td>
                <td><span class="sw-cell-label" aria-hidden="true">Seats</span>{{ plan.seats }}</td>
                <td>{{ plan.fit }}</td>
                <td>
                  <NuxtLink
                    v-if="plan.slug === 'enterprise'"
                    :to="localePath('/contact-sales')"
                    class="sw-row-cta"
                    data-conversion-event="demo_request_click"
                    :data-conversion-label="`Switch from HoneyBook plan CTA: ${plan.name}`"
                    data-conversion-location="/switch/honeybook"
                  >Talk to sales</NuxtLink>
                  <a
                    v-else
                    :href="addReferralToUrl(`https://app.workspace369.com/signup?plan=${plan.slug}`)"
                    class="sw-row-cta"
                    :class="{ 'sw-row-cta-filled': plan.popular }"
                    data-conversion-event="start_free_click"
                    :data-conversion-label="`Switch from HoneyBook plan CTA: ${plan.name}`"
                    data-conversion-location="/switch/honeybook"
                  >Try {{ plan.name }}</a>
                  <TrialDisclosure v-if="plan.slug !== 'enterprise'" compact :plan="plan.slug" on-light />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="sw-plans-note">
          Prices in USD. Full feature matrix on the
          <NuxtLink :to="localePath('/pricing')">pricing page</NuxtLink>.
        </p>
      </div>
    </section>

    <!-- VOICES -->
    <section class="sw-voices">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>In their words</p>
          <h2>Who's switching — and why</h2>
          <span class="sw-head-copy">
            From HoneyBook's own community forum:
          </span>
        </header>

        <div class="sw-quotes">
          <figure v-for="voice in voices" :key="voice.quote" class="sw-quote">
            <blockquote>“{{ voice.quote }}”</blockquote>
            <figcaption>
              — {{ voice.attribution }},
              <a :href="voice.source" target="_blank" rel="noopener noreferrer">{{ voice.sourceLabel }}</a>
            </figcaption>
          </figure>
        </div>

        <p class="sw-voices-fair">
          To be fair: plenty of solo creatives still love HoneyBook, and if it fits your business,
          staying is a fine choice. This page is for the people it stopped fitting in 2026.
        </p>
      </div>
    </section>

    <!-- FAQ -->
    <section class="sw-faq">
      <div class="sw-wrap sw-wrap-narrow">
        <header class="sw-head">
          <p>FAQ</p>
          <h2>Switching questions, answered straight</h2>
        </header>
        <div class="sw-faq-list">
          <details v-for="item in faq" :key="item.q" class="sw-faq-item">
            <summary>
              <h3>{{ item.q }}</h3>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </summary>
            <div class="sw-faq-answer">
              <p>{{ item.a }}</p>
              <p v-if="item.linkHref" class="sw-faq-more">
                <a :href="item.linkHref" target="_blank" rel="noopener noreferrer">{{ item.linkLabel }} ↗</a>
              </p>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- INTERNAL LINKS -->
    <section class="sw-explore">
      <div class="sw-wrap">
        <header class="sw-head sw-head-center">
          <p>Keep exploring</p>
          <h2>Before you decide</h2>
        </header>
        <div class="sw-explore-grid">
          <NuxtLink v-for="card in explore" :key="card.path" :to="localePath(card.path)" class="sw-explore-card">
            <h3>{{ card.title }}</h3>
            <p>{{ card.copy }}</p>
            <span>Read →</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="sw-band" data-sticky-cta-hide>
      <div class="sw-wrap">
        <h2>Ready to make the move?</h2>
        <p>
          Start the trial, run the checklist above, and cancel HoneyBook when the last open
          invoice there is paid.
        </p>
        <div class="sw-cta sw-cta-center">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup?plan=voyager')"
            class="sw-btn sw-btn-light"
            data-conversion-event="start_free_click"
            data-conversion-label="Switch from HoneyBook final CTA"
            data-conversion-location="/switch/honeybook"
            :data-conversion-variant="ctaVariant || undefined"
          >Start free trial</a>
          <NuxtLink :to="localePath('/compare/honeybook')" class="sw-link sw-link-light">
            See the full comparison →
          </NuxtLink>
        </div>
        <TrialDisclosure plan="voyager" on-dark centered />
      </div>
    </section>
    <PhoneStickyCta href="https://app.workspace369.com/signup?plan=voyager" plan="voyager" label="Switch from HoneyBook" location="/switch/honeybook" watch=".sw-hero .sw-cta" />
  </main>
</template>

<script setup>
import { switchFromHoneyBookTour } from '~/data/walkthroughs/screen-tours'
import "~/assets/css/switch-dark.css";
const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();
// The hero runs the CTA copy test (composables/useCtaTest.ts); the server renders the first arm,
// and the button's min-width fits the longer arm so the swap after hydration doesn't resize it.
const { variant: ctaVariant, label: ctaLabel } = useCtaTest("/switch/honeybook");
const siteUrl = "https://workspace369.com";
const pageUrl = `${siteUrl}/switch/honeybook/`;
const metaDescription =
  "Switch from HoneyBook to Workspace369: AI Import maps your CSV or XLSX of clients, invoices and proposals and shows the usage it needs first. Checklist inside.";

// Tool-neutral exit steps. Every HoneyBook fact is from help.honeybook.com, read October 31, 2026:
// contacts export (2650752), save a smart file as PDF (5949670), reports and desktop downloads (2209117),
// cancel membership (2584102).
const leaveSteps = [
  {
    title: "Don't cancel first",
    copy: "Cancelling blocks new files, projects and messages. HoneyBook says you keep access to existing projects afterwards and that cancelling doesn't delete the account, but finish active work before you cancel.",
  },
  {
    title: "Export your contacts",
    copy: "In HoneyBook, open Clients, then Contacts, select the triple-dot icon and choose Download spreadsheet. The CSV holds each contact's name, email, phone number, address, notes and created date.",
  },
  {
    title: "Save the files you need, one at a time",
    copy: "Contracts and invoices are smart files: open each one, select the triple-dot icon, then Print file and Save as PDF. Download the reports you need from Reports, mostly in a desktop browser. HoneyBook's help center documents no single export-everything button.",
  },
  {
    title: "Run both tools for a few weeks",
    copy: "Start new clients in the new tool and finish open projects in HoneyBook. Let the last open invoices get paid where they were sent.",
  },
  {
    title: "Rebuild templates and automations by hand",
    copy: "List the emails, forms and files you actually send, then re-create those in the new tool. Plan on rebuilding automations by hand too.",
  },
  {
    title: "Cancel last",
    copy: "When the last invoice is paid and every export opens, go to Company Settings, then Membership, then Cancel Plan. If you subscribed through the App Store or Google Play, cancel there. The account stays usable until the end of the billing period.",
  },
];

const steps = [
  {
    title: "Start the trial and set up your services and prices",
    copy: "The 14-day trial is enough time to move. Add your services, packages, and rates first — proposals and invoices take minutes after that.",
  },
  {
    title: "Run AI Import for each record type",
    copy: "Upload a CSV or XLSX for clients, products, expenses, invoices or proposals. AI Import maps your columns, you review the mapping, and the estimated usage is shown before you start.",
  },
  {
    title: "Connect Stripe for payments",
    copy: "Workspace369 payments run on Stripe. Connect once and every invoice goes out with a payment link — plus payment plans on Specialist and above.",
  },
  {
    title: "Set up your inbox, SMS, and voice",
    copy: "Connect your business mailbox for email over SMTP and IMAP (Gmail) or by forwarding (Microsoft 365); one-click Gmail and Microsoft 365 sign-in is coming soon. Two-way SMS, the shared team inbox and a business number with calling and voicemail start on Voyager ($74/month, 2 seats); IVR, call queues and call recording start on Specialist ($149/month, 10 seats).",
  },
  {
    title: "Invite your team",
    copy: "Add your seats, then point everyone at the shared inbox and the client records. Anything clients send now lands where the whole team can see it.",
  },
];

const plans = [
  {
    name: "Cadet",
    slug: "cadet",
    price: "$29",
    seats: "1",
    fit: "You're solo and don't need texting yet. Email automations included (up to 3 active workflows).",
    popular: false,
  },
  {
    name: "Voyager",
    slug: "voyager",
    price: "$74",
    seats: "2",
    fit: "You want to text and call clients — this is the entry point for SMS, calling and voicemail, with SMS steps in up to 10 active workflows.",
    popular: false,
  },
  {
    name: "Specialist",
    slug: "specialist",
    price: "$149",
    seats: "10",
    fit: "You want IVR, call queues and call recording, payment plans, and up to 25 active workflows.",
    popular: true,
  },
  {
    name: "Commander",
    slug: "commander",
    price: "$299",
    seats: "20",
    fit: "You want unlimited workflows with AI drafting, plus full accounting.",
    popular: false,
  },
  {
    name: "Enterprise",
    slug: "enterprise",
    price: "$699",
    seats: "50",
    fit: "You run several teams and need 50 seats in one plan.",
    popular: false,
  },
];

const voices = [
  {
    quote: "Every hour I had logged and not yet invoiced is gone.",
    attribution: "a HoneyBook user in the Time Tracker sunset thread",
    source: "https://community.honeybook.com/honeybook-product-talk-7/sunset-of-time-tracker-6769?page=2",
    sourceLabel: "HoneyBook Community, page 2 (September 8-9, 2026), reacting to the retirement announcement",
  },
  {
    quote: "A blatant money grab.",
    attribution: "a HoneyBook user on the $8/month texting add-on",
    source: "https://community.honeybook.com/honeybook-product-talk-7/new-texting-features-worth-it-6962",
    sourceLabel: "HoneyBook Community",
  },
];

const faq = [
  {
    q: "Is Workspace369 a good HoneyBook alternative for a small agency team?",
    a: "Yes. HoneyBook Essentials lets you add up to two team members, so a team of five needs Premium at $129/mo on monthly billing (checked October 24, 2026). Workspace369 includes 2 seats on Voyager ($74/mo) and 10 on Specialist ($149/mo), with extra seats at $10/mo. From Voyager you also get client-scoped access for contractors and a shared inbox with texts and calls, plus a one-time $50 carrier compliance setup when you claim a number; texts and calls draw on the plan's monthly usage.",
  },
  {
    q: "Is Workspace369 cheaper than HoneyBook?",
    a: "For one person, yes: Workspace369 Cadet is $29/mo ($23.25 billed yearly) against HoneyBook Starter at $36/mo ($29 billed yearly). For five people, Workspace369 Voyager is $104/mo (2 seats included, 3 extra at $10 each) against HoneyBook Premium at $129/mo on monthly billing. It is not the cheapest HoneyBook alternative overall; the HoneyBook alternatives guide under \"Before you decide\" compares the lower-priced tools. HoneyBook prices checked October 24, 2026.",
  },
  {
    q: "What does a team of 2 or 3 pay on Workspace369?",
    a: "On Voyager, 2 people pay $74 a month and 3 people pay $84: 2 seats are included and the third is a $10 seat. Billed yearly, Voyager is $59.25 a month and the extra seat is still $10, so 3 people pay $69.25 a month. HoneyBook Essentials is $59 a month on monthly billing (checked October 27, 2026) and lets you add up to two team members.",
  },
  {
    q: "Does the Workspace369 trial need a card?",
    a: "Yes. Cadet through Commander start with a 14-day trial, and you add a card to start. Nothing is charged during the 14 days, and cancelling in Settings → Billing before the trial ends costs nothing. HoneyBook's trial asks for no card (its pricing page, checked October 24, 2026), so export your HoneyBook data first, then start the Workspace369 trial and import it.",
  },
  {
    q: "Should I cancel HoneyBook before I switch?",
    a: "No. Start the 14-day Workspace369 trial, import your clients, and send new proposals and invoices from Workspace369. Keep HoneyBook open until the last open invoice is paid, because saved client cards don't transfer: each client re-enters a card the first time they pay a Workspace369 invoice.",
  },
  {
    q: "How long does switching take?",
    a: "Most teams finish in a few hours: AI Import brings in your clients, products, invoices and proposals from spreadsheets, and you re-create the templates you actually send, which is usually the longest part. You don't need to move closed projects — they stay in HoneyBook as your archive.",
  },
  {
    q: "Can I import my HoneyBook data automatically?",
    a: "From a spreadsheet, yes. There is no one-click import, but AI Import looks for HoneyBook exports: when it spots one it names HoneyBook as the source, and you can pick HoneyBook yourself if it doesn't. To start, download your contact list (Clients, then Contacts, then Download spreadsheet) or a report as CSV and upload it. Before anything is saved, it shows how the columns map and the AI usage it needs, and you review the mapping. The 14-day trial includes enough AI usage to import a few hundred clients.",
  },
  {
    q: "What does AI Import cost?",
    a: "It draws on your plan's monthly AI usage, and the usage it needs is shown before you start the import, so you can decide before anything runs. Email credits are separate.",
  },
  {
    q: "What happens to my clients' saved cards?",
    a: "They stay in HoneyBook — saved payment methods can't be transferred between systems. The first time a client pays a Workspace369 invoice, they re-enter their card through the new payment link. In practice that's one extra step for the client on their next payment.",
  },
  {
    q: "Do I lose my HoneyBook history?",
    a: "No. Keep your HoneyBook account open until the last open invoice is paid; your history stays viewable under HoneyBook's own terms while the account is active. Time Tracker entries are a special case: HoneyBook switched Time Tracker off on September 8, 2026 and brought it back on September 15, 2026, and a HoneyBook employee confirmed previous entries were kept. Export your time logs anyway.",
    linkHref: "https://community.honeybook.com/honeybook-product-talk-7/sunset-of-time-tracker-6769",
    linkLabel: "HoneyBook's Time Tracker retirement announcement",
  },
  {
    q: "Which plan do I need for texting and calling?",
    a: "Two-way SMS and a business number with calling and voicemail start on Voyager ($74/month, 2 seats), plus a one-time $50 carrier compliance setup when you claim a number; texts and calls draw on the plan's monthly usage. IVR, call queues and call recording start on Specialist ($149/month, 10 seats). Workspace369 doesn't sell SMS as a separate add-on — it's built into the plan.",
  },
];

const explore = [
  {
    title: "AI Import",
    copy: "How the CSV import maps columns and shows the cost first.",
    path: "/integrations/csv-import",
  },
  {
    title: "HoneyBook alternatives",
    copy: "Every tool people compare against HoneyBook, with current prices and who each one fits.",
    path: "/alternatives/honeybook",
  },
  {
    title: "How to export everything from HoneyBook",
    copy: "A step-by-step checklist of what HoneyBook's help center says you can export, and what has no documented export.",
    path: "/blog/how-to-export-from-honeybook",
  },
  {
    title: "Workspace369 vs HoneyBook",
    copy: "The feature-by-feature head-to-head, including the 2026 HoneyBook changes.",
    path: "/compare/honeybook",
  },
  {
    title: "Pricing",
    copy: "All five plans, the full feature matrix, and annual billing discounts.",
    path: "/pricing",
  },
  {
    title: "Inbox, SMS & voice",
    copy: "How the shared inbox, two-way texting, and business calling work together.",
    path: "/features/communications",
  },
  {
    title: "Invoices & payments",
    copy: "Payment links, payment plans, deposits, and automated follow-up on every invoice.",
    path: "/features/invoices",
  },
];

useSeoMeta({
  title: "Switch from HoneyBook to Workspace369 | Migration Guide",
  description: metaDescription,
  ogTitle: "Switch from HoneyBook to Workspace369 | Migration Guide",
  ogDescription: metaDescription,
  ogUrl: pageUrl,
  ogType: "website",
  twitterCard: "summary_large_image",
  twitterTitle: "Switch from HoneyBook to Workspace369 | Migration Guide",
  twitterDescription: metaDescription,
});

useHead({
  link: [{ key: "canonical", rel: "canonical", href: pageUrl }],
  script: [
    {
      key: "workspace369-switch-honeybook-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${pageUrl}#webpage`,
            name: "Switch from HoneyBook to Workspace369 | Migration Guide",
            url: pageUrl,
            description: metaDescription,
            dateModified: "2026-10-31",
            isPartOf: { "@id": `${siteUrl}/#website` },
            about: { "@id": `${siteUrl}/#software` },
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
              { "@type": "ListItem", position: 2, name: "Switch from HoneyBook", item: pageUrl },
            ],
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            mainEntity: faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          },
        ],
      }),
    },
  ],
});
</script>

<style scoped>
.sw { background: #fff; color: #111827; }

.sw-wrap {
  max-width: 56rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}
@media (min-width: 640px) { .sw-wrap { padding-inline: 1.75rem; } }

.sw-wrap-narrow { max-width: 44rem; }

.sw-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

/* HERO */
.sw-hero {
  /* Clears the fixed header: 36px offer strip + 64px nav, both scale with text size. */
  padding: 7.5rem 0 3.5rem;
  background:
    radial-gradient(42rem 22rem at 12% -8rem, rgb(14 116 144 / 0.08), transparent 70%);
}
@media (min-width: 768px) { .sw-hero { padding: 8rem 0 4rem; } }

.sw-hero h1 {
  margin-top: 0;
  max-width: 44rem;
  font-size: clamp(2.05rem, 4.6vw, 3.3rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  line-height: 1.05;
  text-wrap: balance;
}

.sw-answer {
  margin-top: 1.2rem;
  max-width: 42rem;
  border-left: 3px solid #0e7490;
  padding-left: 0.9rem;
  color: #1f2937;
  font-size: 1rem;
  line-height: 1.65;
}
.sw-answer-label {
  margin-bottom: 0.2rem;
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sw-lede {
  margin-top: 1.3rem;
  max-width: 42rem;
  color: #4b5563;
  font-size: 1.02rem;
  line-height: 1.75;
}

.sw-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.2rem;
  margin-top: 2rem;
}

.sw-cta-center { justify-content: center; }

.sw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  padding: 0.75rem 1.5rem;
  font-size: 0.94rem;
  font-weight: 800;
  box-shadow: 0 10px 24px -6px rgb(17 24 39 / 0.3);
  transition: background-color 150ms ease, transform 150ms ease;
}

.sw-btn:hover { background: #0e7490; transform: translateY(-1px); }

.sw-btn-light { background: #fff; color: #111827; }
.sw-btn-light:hover { background: #f0f9fc; }

.sw-link { color: #0e7490; font-size: 0.94rem; font-weight: 800; }
.sw-link:hover { text-decoration: underline; }
.sw-link-light { color: #67e8f9; }

.sw-updated {
  margin-top: 1.4rem;
  color: #5f6b7a;
  font-size: 0.78rem;
  font-weight: 600;
}

/* SECTION HEADS */
.sw-head { max-width: 42rem; margin-bottom: 2.25rem; }
.sw-head-center { margin-inline: auto; text-align: center; }

.sw-head p {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.sw-head h2 {
  margin-top: 0.55rem;
  font-size: clamp(1.7rem, 3.2vw, 2.35rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-wrap: balance;
}

.sw-head-copy {
  display: block;
  margin-top: 0.85rem;
  max-width: 40rem;
  color: #4b5563;
  font-size: 0.96rem;
  line-height: 1.7;
}

.sw-head-center .sw-head-copy { margin-inline: auto; }

/* WHAT MOVES */
.sw-moves { padding: 1rem 0 4rem; }
@media (min-width: 768px) { .sw-moves { padding: 1.5rem 0 5rem; } }

.sw-moves-grid {
  display: grid;
  gap: 1rem;
}
@media (min-width: 820px) { .sw-moves-grid { grid-template-columns: 1fr 1fr; } }

.sw-move-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 1.5rem 1.6rem;
}

.sw-move-yes { border-top: 3px solid #0e7490; }
.sw-move-no { border-top: 3px solid #cbd5e1; }

.sw-move-card h3 { font-size: 1.02rem; font-weight: 800; }

.sw-move-card ul {
  margin-top: 0.9rem;
  display: grid;
  gap: 0.75rem;
  list-style: none;
  padding: 0;
}

.sw-move-card li {
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.65;
  padding-left: 1.1rem;
  position: relative;
}

.sw-move-card li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 999px;
  background: #9aa3ad;
}

.sw-move-yes li::before { background: #0e7490; }

.sw-move-card li strong { color: #111827; font-weight: 750; }

.sw-moves-note {
  margin-top: 1.5rem;
  border: 1px solid rgb(14 116 144 / 0.25);
  border-radius: 10px;
  background: #f0f9fc;
  padding: 1.05rem 1.2rem;
  color: #334155;
  font-size: 0.9rem;
  line-height: 1.7;
}

/* CHECKLIST */
.sw-checklist {
  background: #f7f8f9;
  border-block: 1px solid #eef0f3;
  padding: 3.5rem 0;
}
@media (min-width: 768px) { .sw-checklist { padding: 4.5rem 0; } }

.sw-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.8rem;
}

.sw-step {
  display: flex;
  gap: 1.1rem;
  align-items: flex-start;
  border: 1px solid #e5e7eb;
  border-radius: 0.95rem;
  background: #fff;
  padding: 1.25rem 1.4rem;
}

.sw-step-num {
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  flex-shrink: 0;
  border-radius: 999px;
  background: #0e7490;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 900;
  box-shadow: 0 0 0 4px rgb(14 116 144 / 0.12);
}

.sw-step h3 { font-size: 0.99rem; font-weight: 800; line-height: 1.35; }

.sw-step p {
  margin-top: 0.4rem;
  color: #4b5563;
  font-size: 0.89rem;
  line-height: 1.65;
}

/* PLANS */
/* PHONE AND IPAD (MobileAppProof); white like the plans below, so a rule splits them */
.sw-tour { padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-tour { padding: 4.5rem 0; } }
.sw-tour .st { margin-top: 2rem; }

.sw-mobile { padding: 3.5rem 0; border-bottom: 1px solid #eef0f3; }
@media (min-width: 768px) { .sw-mobile { padding: 4.5rem 0; } }

.sw-plans { padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-plans { padding: 4.5rem 0; } }

.sw-table-scroll { position: relative; overflow-x: auto; }

.sw-table {
  width: 100%;
  min-width: 44rem;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.sw-table th,
.sw-table td {
  padding: 0.95rem 1.1rem;
  text-align: left;
  font-size: 0.89rem;
  border-bottom: 1px solid #eef0f3;
  vertical-align: middle;
}

.sw-table thead th {
  background: #f7f8f9;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.sw-table tbody tr:last-child th,
.sw-table tbody tr:last-child td { border-bottom: none; }

.sw-table tbody th {
  color: #111827;
  font-size: 0.94rem;
  font-weight: 850;
  white-space: nowrap;
}

.sw-table tbody td { color: #4b5563; line-height: 1.5; }

.sw-table tbody td:nth-child(2) {
  color: #111827;
  font-weight: 800;
  white-space: nowrap;
}

.sw-table tbody td:nth-child(3) { white-space: nowrap; }

/* Plan table: give "Pick it if…" room so rows don't grow tall on phones. */
.sw-table:not(.sw-seat-table) tbody td:nth-child(4) { min-width: 20rem; }
.sw-table:not(.sw-seat-table) .sw-popular-tag { display: block; width: max-content; margin: 0.35rem 0 0; }

.sw-row-popular { background: #f0f9fc; }

.sw-popular-tag {
  display: inline-block;
  margin-left: 0.45rem;
  border-radius: 4px;
  background: #0e7490;
  color: #fff;
  padding: 0.14rem 0.5rem;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  vertical-align: middle;
}

.sw-row-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #0e7490;
  border-radius: 6px;
  padding: 0.42rem 0.95rem;
  color: #0e7490;
  font-size: 0.8rem;
  font-weight: 800;
  white-space: nowrap;
  transition: background-color 150ms ease, color 150ms ease;
}

.sw-row-cta:hover { background: #0e7490; color: #fff; }

.sw-row-cta-filled { background: #0e7490; color: #fff; }
.sw-row-cta-filled:hover { background: #155e75; border-color: #155e75; }

/* The plan's own short trial line, directly under its Try button (B1.1). */
.sw-table td :deep(.trial-disclosure.compact) { margin: 0.4rem 0 0; line-height: 1.35; }

.sw-cell-label { display: none; }

/* Phones: the 44rem table hid every Try button off screen at 390px, so each plan becomes a card:
   name and price on top, seats, "Pick it if…", then a full-width Try button with its trial line. */
@media (max-width: 639px) {
  .sw-table:not(.sw-seat-table) { min-width: 0; border: 0; border-radius: 0; overflow: visible; background: none; }
  .sw-table:not(.sw-seat-table) thead {
    position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;
  }
  .sw-table:not(.sw-seat-table) tbody { display: grid; gap: 0.75rem; }
  .sw-table:not(.sw-seat-table) tbody tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.45rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    padding: 1rem 1.1rem 1.1rem;
  }
  .sw-table:not(.sw-seat-table) tbody tr.sw-row-popular { background: #f0f9fc; }
  .sw-table:not(.sw-seat-table) tbody :is(th, td) { display: block; padding: 0; border: 0; min-width: 0; }
  .sw-table:not(.sw-seat-table) tbody th { grid-column: 1; align-self: center; white-space: normal; }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(2) {
    grid-column: 2; grid-row: 1; align-self: center; text-align: right; font-size: 1.35rem;
  }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(3) { grid-column: 1 / -1; }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(4) { grid-column: 1 / -1; min-width: 0; font-size: 0.9rem; }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(5) { grid-column: 1 / -1; margin-top: 0.35rem; }
  .sw-table:not(.sw-seat-table) .sw-row-cta { display: flex; width: 100%; min-height: 2.75rem; font-size: 0.92rem; }
  .sw-table:not(.sw-seat-table) td :deep(.trial-disclosure.compact) { text-align: center; }
  .sw-table:not(.sw-seat-table) .sw-cell-label {
    display: block;
    color: #6b7280;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(3) .sw-cell-label { display: inline; margin-right: 0.4rem; }
}

.sw-plans-note {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.82rem;
}

.sw-plans-note a { color: #0e7490; font-weight: 750; }
.sw-plans-note a:hover { text-decoration: underline; }
.sw-head-copy a { color: #0e7490; text-decoration: underline; }

/* STAY */
.sw-stay { background: #f7f8f9; border-block: 1px solid #eef0f3; padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-stay { padding: 4.5rem 0; } }
.sw-stay-card { border: 1px solid #e5e7eb; border-top: 3px solid #cbd5e1; border-radius: 10px; background: #fff; padding: 1.5rem 1.6rem; }
.sw-stay-card ul { display: grid; gap: 0.75rem; list-style: none; padding: 0; margin: 0; }
.sw-stay-card li { color: #4b5563; font-size: 0.92rem; line-height: 1.65; padding-left: 1.1rem; position: relative; }
.sw-stay-card li::before { content: ""; position: absolute; left: 0; top: 0.55em; width: 0.35rem; height: 0.35rem; border-radius: 999px; background: #9aa3ad; }
.sw-stay-card li strong { color: #111827; font-weight: 750; }

/* VOICES */
.sw-voices {
  background: #f7f8f9;
  border-block: 1px solid #eef0f3;
  padding: 3.5rem 0;
}
@media (min-width: 768px) { .sw-voices { padding: 4.5rem 0; } }

.sw-quotes {
  display: grid;
  gap: 1rem;
}
@media (min-width: 860px) { .sw-quotes { grid-template-columns: repeat(3, 1fr); } }

.sw-quote {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 1.5rem 1.6rem;
  margin: 0;
}

.sw-quote blockquote {
  margin: 0;
  color: #111827;
  font-size: 1.02rem;
  font-weight: 750;
  line-height: 1.5;
}

.sw-quote figcaption {
  margin-top: 1rem;
  color: #5f6b7a;
  font-size: 0.78rem;
  line-height: 1.55;
}

.sw-quote figcaption a { color: #0e7490; font-weight: 750; }
.sw-quote figcaption a:hover { text-decoration: underline; }

.sw-voices-fair {
  margin-top: 1.6rem;
  max-width: 40rem;
  color: #4b5563;
  font-size: 0.88rem;
  line-height: 1.7;
}

/* FAQ */
.sw-faq { padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-faq { padding: 4.5rem 0; } }

.sw-faq-list { display: grid; gap: 0.7rem; }

.sw-faq-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: border-color 150ms ease;
}

.sw-faq-item[open] { border-color: rgb(14 116 144 / 0.4); }

.sw-faq-item summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  padding: 1.05rem 1.2rem;
  list-style: none;
}

.sw-faq-item summary::-webkit-details-marker { display: none; }

.sw-faq-item summary h3 { font-size: 0.97rem; font-weight: 800; line-height: 1.35; }

.sw-faq-item summary svg {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: #9aa3ad;
  transition: transform 160ms ease;
}

.sw-faq-item[open] summary svg { transform: rotate(180deg); color: #0e7490; }

.sw-faq-answer {
  border-top: 1px solid #eef0f3;
  padding: 1rem 1.2rem 1.2rem;
}

.sw-faq-answer p {
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.7;
}

.sw-faq-more { margin-top: 0.6rem; }

.sw-faq-more a { color: #0e7490; font-weight: 750; font-size: 0.86rem; }
.sw-faq-more a:hover { text-decoration: underline; }

/* EXPLORE */
.sw-explore {
  background: #f7f8f9;
  border-block: 1px solid #eef0f3;
  padding: 3.5rem 0;
}
@media (min-width: 768px) { .sw-explore { padding: 4.5rem 0; } }

.sw-explore-grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 760px) { .sw-explore-grid { grid-template-columns: 1fr 1fr; } }
@media (min-width: 1024px) { .sw-explore-grid { grid-template-columns: repeat(3, 1fr); } }

.sw-explore-card {
  display: grid;
  gap: 0.45rem;
  align-content: start;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 1.4rem 1.5rem;
  transition: border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;
}

.sw-explore-card:hover {
  border-color: rgb(14 116 144 / 0.4);
  transform: translateY(-2px);
  box-shadow: 0 14px 32px -10px rgb(15 23 42 / 0.1);
}

.sw-explore-card h3 { font-size: 0.98rem; font-weight: 800; }

.sw-explore-card p { color: #4b5563; font-size: 0.85rem; line-height: 1.6; }

.sw-explore-card span {
  margin-top: 0.35rem;
  color: #0e7490;
  font-size: 0.82rem;
  font-weight: 800;
}

/* FINAL CTA */
.sw-band {
  background:
    radial-gradient(38rem 20rem at 50% 0%, rgb(14 116 144 / 0.22), transparent 70%),
    #020617;
  color: #fff;
  padding: 4.5rem 0;
  text-align: center;
}

.sw-band h2 {
  font-size: clamp(1.8rem, 3.4vw, 2.6rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  text-wrap: balance;
}

.sw-band p {
  margin: 0.9rem auto 0;
  max-width: 32rem;
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1.65;
}
/* Phones: the final band's trial line sits directly under its button (legal B1.1) and the comparison link moves
   below it instead of wrapping in between. Wider screens keep the button row. */
@media (max-width: 767px) {
  .sw-band .sw-wrap { display: flex; flex-direction: column; align-items: center; }
  .sw-band .sw-wrap > * { order: 1; }
  .sw-band .sw-cta { display: contents; }
  .sw-band .sw-cta > .sw-btn { order: 2; margin-top: 2rem; }
  /* TrialDisclosure renders a fragment, so it carries no scope id: reach it with :deep(). */
  .sw-band .sw-wrap > :deep(.trial-disclosure) { order: 3; }
  .sw-band .sw-cta > .sw-link { order: 4; margin-top: 0.9rem; }
}
</style>
